import React, { useEffect } from 'react';
import { ErrorMessage, FlexRow, Grid, Header3Text } from 'ui-toolkit';
import { BlockRedirection } from 'components/cdBuilder/BlockRedirection';
import { FieldArray, Field, FieldProps } from 'formik';
import { IEscrowWireDisbursementsData } from 'types/order/escrow/IEscrowWireDisbursementsData';
import { CreateEditWireDisbursementsRow } from './CreateEditWireDisbursementsRow';
import { CheckForAvailableFundsModal } from '../../escrowCreateEdit/checkForAvailableFundsModal/CheckForAvailableFundsModal';
import { useSelector } from 'react-redux';
import { RootStore } from 'store/types';
import { HasPermissionButton } from '../../shared/HasPermissionButton';
import {
  deleteWireDisbursementAction,
  deleteWireDisbursementSlice,
} from 'store/order/escrow/disbursements/wireDisbursements/deleteWireDisbursementSlice';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { useSendWireDisbursement } from '../../hooks/useSendWireDisbursement';
import { assertDefined } from 'utilities/Assertions';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { useAppDispatch } from '../../../../../store/store';

export interface ICreateEditWireDisbursementsContainerProps {
  dirty: boolean;
  onTransactionUpdate: () => void;
  availableAmountDollars: number;
}

export const CreateEditWireDisbursementsContainer: React.FC<ICreateEditWireDisbursementsContainerProps> = ({
  dirty,
  onTransactionUpdate,
  availableAmountDollars,
}) => {
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);

  const {
    showWarningModal,
    setShowWarningModal,
    sendWireId,
    onSendWireDisbursement: handleSendWireDisbursement,
  } = useSendWireDisbursement({
    availableAmountDollars,
    orderId: orderId!,
    onTransactionUpdate,
  });

  const dispatch = useAppDispatch();
  const hasEditPermission = useHasPermissions([ePermissions.EscrowWireDisbursementsEdit]);
  const fieldName = 'wireDisbursements';

  const {
    sendWirePush: { fetchStatus, fetchError },
    deleteWireDisbursement: { fetchStatus: deleteFetchStatus },
  } = useSelector((store: RootStore) => store.order.escrow.disbursements);

  useEffect(() => {
    if (deleteFetchStatus.success) {
      onTransactionUpdate();
      dispatch(deleteWireDisbursementSlice.actions.reset({}));
    }
  }, [dispatch, onTransactionUpdate, deleteFetchStatus]);

  const onRemoveWireDisbursement = async (wireToRemove: IEscrowWireDisbursementsData) => {
    if (wireToRemove.id) {
      dispatch(deleteWireDisbursementAction(wireToRemove.id));
      onTransactionUpdate();
    } else {
      dispatch(deleteWireDisbursementSlice.actions.setData({}));
    }
  };

  const onSendWireDisbursement = async (wirePush: IEscrowWireDisbursementsData) => {
    if (wirePush?.amountCents) {
      await handleSendWireDisbursement({ id: wirePush.id, amountCents: wirePush.amountCents });
    }
  };

  return (
    <>
      <BlockRedirection showSaveSpinner={true} showSaveSuccess={true} isDirty={dirty} />
      <FieldArray name={fieldName}>
        {({ push, remove }) => (
          <Grid>
            <FlexRow>
              <Header3Text>Payment Manager Automated Wires</Header3Text>
              <HasPermissionButton
                hasEditPermission={hasEditPermission}
                push={push}
                successMessage={'Add Payment Manager Automated Wire'}
                transferType={'check deposits'}
              />
            </FlexRow>
            <ErrorMessage error={fetchError} />
            <Field name={fieldName}>
              {({ field }: FieldProps<IEscrowWireDisbursementsData[]>) => (
                <>
                  {field.value.map((wire, index) => (
                    <React.Fragment key={wire.id}>
                      <CreateEditWireDisbursementsRow
                        index={index}
                        onDelete={async (index) => {
                          remove(index);
                          await onRemoveWireDisbursement(wire);
                        }}
                        fieldName={fieldName}
                        onSend={() => onSendWireDisbursement(wire)}
                        fetching={fetchStatus.fetching && sendWireId === wire.id}
                        routingNumber={wire.routingNumber}
                      />
                      {showWarningModal && (
                        <CheckForAvailableFundsModal
                          submitData={() => onSendWireDisbursement(wire)}
                          type="wire"
                          key={index}
                          handleClose={() => setShowWarningModal(false)}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </>
              )}
            </Field>
          </Grid>
        )}
      </FieldArray>
    </>
  );
};
